import { useEffect, useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import { PlusIcon, ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { Table, TableMenu, TableColumn, MenuOption } from "../../components/Table";
import {
  FaCalendarAlt, FaFilter, FaSearch, FaTimes
} from "react-icons/fa";

import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { initAllStores } from "../../utils/cache";
import { errorMessage, sendRequest } from "../../utils/api";
import { v4 as uuidv4 } from "uuid";
import FullPageSpinner from "../../components/FullPageSpinner";
import { capitalizeFirst } from "../../utils/string";
import ExpenditureCategory from "../../models/ExpenditureCategory";
import Expenditure from "../../models/Expenditure";
import { formatDate, formatDateTime, todayDate } from "../../utils/date";
import { getUserHotel, terminal } from "../../utils/user-helper";
import User from "../../models/User";

const categoryColumns: TableColumn[] = [
  { key: "serialNo", label: "S/N", },
  { key: "createdAt", label: "Date", },
  { key: "title", label: "Title", getClassName: () => "capitalize" },
];

const columns: TableColumn[] = [
  { key: "title", label: "Title", getClassName: () => "capitalize" },
  { key: "createdAt", label: "created At", responsive: "md" },
  { key: "category", label: "Category", getClassName: () => "capitalize", responsive: "md" },
  { key: "amount", label: "Amount" },
  { key: "addedBy", label: "Added By", responsive: "md", getClassName: () => "capitalize" },
];

const COLORS = [
  "#1f77b4", // Blue
  "#ff7f0e", // Orange
  "#2ca02c", // Green
  "#d62728", // Red
  "#9467bd", // Purple
  "#8c564b", // Brown
  "#e377c2", // Pink
  "#7f7f7f", // Gray
  "#bcbd22", // Lime
  "#17becf", // Cyan
];


export default function Expenditures() {
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const [terminalId] = useState(() => terminal());

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [showDatePopup, setShowDatePopup] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<string | null>(null);

  const [categories, setCategories] = useState<ExpenditureCategory[]>([]);
  const [expenditures, setExpenditures] = useState<Expenditure[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [showCategoryList, setShowCategoryList] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [addingNewCategory, setAddingNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  const [totalCartDesc, setTotalCartDesc] = useState("Total This Month");
  const [accExpenseSum, setAccExpenseSum] = useState<number | null>(null);

  const [user, setUser] = useState<User>({} as User);

  // Fetch data on load
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const init = async () => {
      // const cache = await initAllStores();
      const data = await getUserHotel();
      // const hotel: Hotel | null = await cache.getBy("data", { id: "hotel" });

      if (!data) {
        return openModal(
          "Session Expired ⚠️",
          "We couldn’t find your user information. Please log in again to continue.",
          () => {
            closeModal();
            window.location.href = "/login";
          },
          false,
          "Login Again"
        );
      }

      const { user } = data;
      setUser(user);
    };
    init();
  }, []);

  const getExpenseCategory = (
    expenseId: string
  ): ExpenditureCategory | undefined => {
    const category = categories.find(
      cat => cat.id === expenseId
    );
    return category;
  }

  const fetchData = async (start?: string, end?: string) => {
    setLoading(true);

    try {
      let url = "/expenditures";

      const params = new URLSearchParams({ terminal: terminalId });

      // 👉 append date params only if provided
      if (start && end) {
        params.set("start", start);
        params.set("end", end);

        setTotalCartDesc(`${formatDate(start)} - ${formatDate(end)}`);
      }

      url += `?${params.toString()}`;

      const response = await sendRequest("GET", url);

      if (start && end) {
        const accExpenseSum = response.expenditures.reduce(
          (total: number, exp: Expenditure) => total + exp.amount,
          0
        );
        setAccExpenseSum(accExpenseSum);
      }

      const fetchedCategories = response.category || [];
      setCategories(
        [...fetchedCategories].sort((a, b) =>
          a.title.localeCompare(b.title)
        )
      );


      // console.log({ expense: response.expenditures, category: response.category });

      setExpenditures(
        (response.expenditures || []).map((ex: Expenditure) => ({
          ...ex,
          addedBy: `${ex?.recordBy?.firstName ?? ""} ${ex?.recordBy?.lastName ?? ""}`,
          createdAt: formatDateTime(ex.createdAt),
          amunt: ex?.amount?.toLocaleString(),
          category: fetchedCategories.find(
            (cat: ExpenditureCategory) =>
              cat.id === ex.expenditureCategoryId
          )?.title,
        }))
      );
    } catch (err: any) {
      console.log(err)

      showToast("Failed to load data", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Filtered expenditures
  const filteredExpenditures = useMemo(() => {
    return expenditures.filter((exp) => {
      const matchesSearch = exp.title.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = selectedCategory
        ? categories.find((c) => c.id === exp.expenditureCategoryId)?.title === selectedCategory
        : true;
      // const matchesStartDate = startDate ? new Date(exp.createdAt) >= new Date(startDate) : true;
      // const matchesEndDate = endDate ? new Date(exp.createdAt) <= new Date(endDate) : true;
      // return matchesSearch && matchesCategory && matchesStartDate && matchesEndDate;
      return matchesSearch && matchesCategory;
    });
  }, [expenditures, search, selectedCategory, categories]);

  // Pie chart data
  const pieData = useMemo(() => {
    return categories.map((cat) => {
      const total = expenditures
        .filter((exp) => exp.expenditureCategoryId === cat.id)
        .reduce((sum, exp) => sum + exp.amount, 0);
      return { name: cat.title, value: total };
    });
  }, [categories, expenditures]);

  // Line chart data
  const lineData = useMemo(() => {
    const map: Record<string, number> = {};
    expenditures.forEach((exp) => {
      const date = exp.createdAt.split("T")[0]; // group by day
      if (!map[date]) map[date] = 0;
      map[date] += exp.amount;
    });
    return Object.entries(map)
      .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
      .map(([date, amount]) => ({ date, amount }));
  }, [expenditures]);

  // Summary cards
  const totalThisMonth = useMemo(() => {
    const now = new Date();
    return expenditures
      .filter((exp) => {
        const d = new Date(exp.createdAt);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((sum, exp) => sum + exp.amount, 0);
  }, [expenditures]);

  const todayExpenses = useMemo(() => {
    const today = new Date().toDateString();
    return expenditures
      .filter((exp) => new Date(exp.createdAt).toDateString() === today)
      .reduce((sum, exp) => sum + exp.amount, 0);
  }, [expenditures]);

  // Save expense
  const handleSave = async () => {
    const expenseCategory = categories.find((cat) => cat.id === category);
    const categoryId = expenseCategory?.id ?? uuidv4();

    const data = {
      expense: {
        id: editingId || uuidv4(),
        title, amount, description,
        expenditureCategoryId: categoryId,
        terminal: terminalId,
      },
      category: expenseCategory
        ? expenseCategory
        : { id: categoryId, title: newCategory },
      isNew: addingNewCategory,
    };

    const missing: string[] = [];
    if (!data.expense.title) missing.push("Title");
    if (!data.expense.amount) missing.push("Amount");
    if (!data.category.title) missing.push("Category");

    if (missing.length > 0) {
      return openModal(
        "Missing Fields",
        `Please fill in: ${missing.join(", ")}`,
        closeModal,
        false,
        "Close"
      );
    }

    if (addingNewCategory) {
      const isExist = categories.find(
        (cat) => cat.title.toLowerCase() === data.category.title.toLowerCase()
      );
      if (isExist) {
        showToast(
          `Category "${capitalizeFirst(data.category.title)}" already exists.`,
          { type: "error" }
        );
        return;
      }
    }

    openModal(
      `${isEdit ? "Update" : "Add New"} Expense`,
      `Are you sure you want to ${isEdit ? "Update" : "Add New"} expenditure record?`,
      async () => {
        setLoading(true);
        try {
          const cache = await initAllStores();
          const { id, ...rest } = data.expense;
          const clean = {
            ...rest,
            amount: Number(rest.amount),
          };

          data.category.terminal = terminalId;
          data.expense.terminal = terminalId;

          if (!isEdit) {
            await sendRequest("POST", "/expenditures", data);
            await Promise.all([
              cache.saveOne("expenditures", data.expense),
              cache.saveOne("expenditureCategories", data.category),
            ]);

            fetchData();
          } else {
            await sendRequest("PUT", `/expenditures/${editingId}/update`, clean);

            // if (editingId) {
            //   await cache.updateManyBy("expenditures", [editingId], clean);
            // }

            setExpenditures(prev =>
              prev.map(item =>
                item.id === editingId
                  ? {
                    ...item,
                    ...clean,
                    recordBy: user,
                    category: getExpenseCategory(categoryId)?.title
                  }
                  : item
              )
            );
          }

          showToast(`Expenditure ${isEdit ? "updated" : "added"} successfully`, {
            type: "success",
          });

          setTitle("");
          setCategory("");
          setAmount("");
          setDescription("");
          setNewCategory("");
          setAddingNewCategory(false);
          setShowModal(false);

          // fetchData();
        } catch (error) {
          console.error(error);
          showToast("An error occurred. Try again.", { type: "error" });
        } finally {
          setLoading(false);
        }
      },
      true,
      isEdit ? "Yes, Update" : "Yes, Save",
      "Cancel",
      "blue"
    );
  };

  const deleteExpenditure = (id: string, type: "expense" | "category") => {

    const data = type === "expense"
      ? {
        storeName: "expenditures",
        url: `/expenditures/${id}/delete`,
        warningTitle: "Delete Expenditure",
        warningMsg:
          "Are you sure you want to delete this expenditure? This action cannot be undone.",
      }
      : {
        storeName: "expenditureCategories",
        url: `/expenditures/${id}/delete-category`,
        warningTitle: "Delete Expenditure Category",
        warningMsg: (
          "Deleting this category will also permanently remove all expenditures linked to it. "
          + "This action cannot be undone. Are you sure you want to continue?"
        )
      };
    openModal(
      data.warningTitle,
      data.warningMsg,
      async () => {
        setLoading(true);
        try {
          const cache = await initAllStores();


          // Call API to delete
          await sendRequest("DELETE", data.url);

          // Remove from cache
          await cache.deleteOne(data.storeName, id);

          // Remove from local state
          if (type === "expense") {
            setExpenditures((prev) => prev.filter((exp) => exp.id !== id));
            showToast("Expenditure deleted successfully", { type: "success" });
          } else {
            setCategories((prev) => prev.filter((exp) => exp.id !== id));
            const expenses = await cache.getAllBy(
              "expenditures", { expenditureCategoryId: id }
            );
            for (const exp of expenses) {
              await cache.deleteOne("expenditures", exp.id);
              setExpenditures((prev) => prev.filter((e) => e.id !== exp.id));
            }
            showToast("Expenditure category deleted successfully", { type: "success" });
          }
        } catch (error: any) {
          console.error(error);
          const err = errorMessage(error.status);
          if (err) openModal(err.title, err.msg, closeModal, false, "Close");
        } finally {
          setLoading(false);
        }
      },
      true,
      "Yes, Delete",
      "Cancel",
      "red"
    );
  };

  // const updateCategory = (id: string) => {
  //   openModal(
  //     "Update Expense Category",
  //     "Are you sure you want to update this expense category?",
  //     async () => {
  //       setLoading(true);
  //       try {
  //         const cache = await initAllStores();

  //         // Call API to delete
  //         await sendRequest("PUT", `/expenditures/${id}/delete`);

  //         // Update from cache
  //         await cache.updateManyBy("expenditureCategories", [id], { title: "" });

  //         setCategories(prev =>
  //           prev.map(item =>
  //             item.id === id
  //               ? {
  //                 ...item,
  //                 title: "iig"
  //               }
  //               : item
  //           )
  //         );

  //         showToast("Expenditure updated successfully", { type: "success" });
  //       } catch (error: any) {
  //         const err = errorMessage(error.status);
  //         if (err) openModal(err.title, err.msg, closeModal, false, "Close");
  //       } finally {
  //         setLoading(false);
  //       }
  //     },
  //     true,
  //     "Yes, Update",
  //     "Cancel",
  //     "red"
  //   );
  // };

  return (
    <div className="flex-1 p-4 md:p-6 space-y-6 overflow-y-auto">
      <FullPageSpinner show={loading} />

      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center justify-between w-full">
          <h1 className="text-3xl font-bold text-gray-800">Expenditures</h1>

          <button
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 text-sm font-medium rounded-md hover:bg-blue-700 transition"
            onClick={() => {
              setIsEdit(false);
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <PlusIcon className="w-4 h-4" />
            Add Expense
          </button>

        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded shadow">
          <p className="text-gray-500">{totalCartDesc}</p>
          {/* < p className="text-gray-500">Feb 3, 2026 - Feb 10, 2026</p> */}
          <p className="text-xl font-bold">₦{(accExpenseSum ?? totalThisMonth).toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <p className="text-gray-500">Today’s Expenses</p>
          <p className="text-xl font-bold">₦{todayExpenses.toLocaleString()}</p>
        </div>
        <div
          className="bg-white p-4 rounded shadow cursor-pointer"
          onClick={() => setShowCategoryList(true)}
        >
          <p className="text-gray-500">View All Categories</p>
          <p className="text-xl font-bold">{categories.length}</p>
        </div>
      </div>

      {/* Search & Filter */}
      {/* --------------------- SEARCH & FILTER ----------------------- */}
      <div className="mb-4 relative w-full">
        {/* Search */}
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {FaSearch({})}
        </span>

        <input
          type="text"
          placeholder="Search expenditures..."
          className="w-full pl-10 pr-10 p-2 border rounded"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Filter pill */}
        {(selectedCategory || selectedDateRange) && (
          <span
            className="absolute right-10 top-1/2 -translate-y-1/2 bg-blue-500 text-white text-sm px-2 py-1 rounded-full cursor-pointer"
            onClick={() => {
              setSelectedCategory(null);
              setSelectedDateRange(null);
              setStartDate("");
              setEndDate("");
              setTotalCartDesc("Total This Month");
              setAccExpenseSum(null);
              fetchData();
            }}
          >
            {(selectedCategory || selectedDateRange) + " ✕"}
          </span>
        )}

        {/* Filter icon */}
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-600"
          onClick={() => setFilterOpen(!filterOpen)}
        >
          {FaFilter({})}
        </span>

        {/* Filter dropdown */}
        {filterOpen && (
          <div className="absolute top-full right-0 mt-2 w-48 bg-white border rounded shadow-lg z-50 max-h-60 overflow-y-auto">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`w-full text-left px-3 py-2 hover:bg-blue-500 hover:text-white ${selectedCategory === cat.title ? "bg-blue-500 text-white" : ""
                  }`}
                onClick={() => {
                  setSelectedCategory(cat.title);
                  setFilterOpen(false);
                }}
              >
                {cat.title}
              </button>
            ))}

            <button
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-100 text-gray-700"
              onClick={() => {
                setShowDatePopup(true);
                setFilterOpen(false);
              }}
            >
              {FaCalendarAlt({})} Date
            </button>
          </div>
        )}

        {/* Date popup */}
        {showDatePopup && (
          <div className="absolute top-full right-0 mt-2 w-64 bg-white border rounded-lg shadow-lg p-4 z-50">
            <h3 className="font-semibold mb-2 text-gray-700">Select Date Range</h3>

            <input
              type="date"
              value={startDate || ""}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border rounded px-3 py-1 mb-2"
            />

            <input
              type="date"
              value={endDate || ""}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border rounded px-3 py-1 mb-3"
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDatePopup(false)}
                className="px-3 py-1 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setSelectedDateRange(`${startDate} → ${endDate}`);
                  setShowDatePopup(false);
                  if (startDate && endDate) fetchData(startDate, endDate);
                }}
                className="px-3 py-1 bg-blue-600 text-white rounded"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto mb-8">
        <Table
          columns={columns}
          data={filteredExpenditures}
          rowsPerPage={10}
          tableMenu={(row: Expenditure, close) => (
            <TableMenu
              row={row}
              options={[
                {
                  label: "Details",
                  onClick: (row: Expenditure) => {
                    setIsEdit(true);
                    setEditingId(row.id);
                    setShowModal(true);
                    setTitle(row.title);
                    setAmount(row.amount);
                    setDescription(row.description || "");

                    const category = getExpenseCategory(row.expenditureCategoryId);
                    if (category) setCategory(category?.id);
                  },
                },
                {
                  label: "Delete",
                  onClick: (row) => {
                    deleteExpenditure(row.id, "expense");
                  },
                  color: "text-red-500",
                },
              ]}
              closeMenu={close}
            />
          )}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* <div className="bg-white p-4 rounded shadow">
          <h2 className="text-lg font-bold mb-4">Expenses by Category</h2>
          <div className="overflow-x-auto">
            <PieChart width={300} height={300}>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {pieData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </div>
        </div> */}

        {/* <div className="bg-white p-4 rounded shadow">
          <h2 className="text-lg font-bold mb-4">Expenses Over Time</h2>
          <div className="overflow-x-auto">
            <div className="min-w-[450px]">
              <LineChart width={450} height={300} data={lineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="amount" stroke="#4F46E5" />
              </LineChart>
            </div>
          </div>
        </div> */}

        {/* ------------------------- MODAL ------------------------------ */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6 relative">
              <FullPageSpinner show={loading} />
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                onClick={() => setShowModal(false)}
              >
                {FaTimes({})}
              </button>

              <h2 className="text-xl font-bold mb-4">
                {/* {isEdit ? "Edit Expense" : "Add Expense"} */}
                {isEdit ? "Expense Details" : "Add Expense"}
              </h2>

              {/* Title */}
              <label className="block mb-2 font-medium">Title</label>
              <input
                type="text"
                placeholder="Diesel Purchase"
                className="w-full border rounded px-3 py-2 mb-3 capitalize"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />

              {/* Category */}
              {!addingNewCategory ? (
                <div className="mb-3">
                  <label className="block mb-1 font-medium">Category</label>

                  <div className="flex gap-2">
                    <select
                      className="flex-1 border rounded px-3 py-2 capitalize"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.title}
                        </option>
                      ))}
                    </select>
{/* 
                    {!isEdit && (
                      <button
                        onClick={() => {
                          setCategory("");
                          setAddingNewCategory(true);
                        }}
                        className="px-3 py-2 bg-green-600 text-white rounded"
                      >
                        + New
                      </button>
                    )} */}
                  </div>
                </div>
              ) : (
                <div className="mb-3">
                  <label className="block mb-1 font-medium">New Category</label>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="New category"
                      className="flex-1 border rounded px-3 py-2 capitalize"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                    />

                    <button
                      className="px-3 py-2 bg-gray-300 rounded"
                      onClick={() => {
                        setNewCategory("");
                        setAddingNewCategory(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Amount */}
              <label className="block mb-2 font-medium">Amount</label>
              <input
                type="number"
                className="w-full border rounded px-3 py-2 mb-3"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
              />

              {/* Description */}
              <label className="block mb-2 font-medium">Description (Optional)</label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              {/* Buttons */}
              <div className="flex justify-end gap-3 mt-4">
                <button
                  className="px-4 py-2 bg-gray-200 rounded"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                  onClick={handleSave}
                >
                  {isEdit ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showCategoryList && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fadeIn">
            <div className="relative w-[90%] max-w-3xl bg-white rounded-lg shadow-xl animate-scaleIn overflow-hidden">

              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 w-full h-1 bg-green-600"></div>

              {/* Header */}
              <div className="flex items-center justify-between border-b p-4 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <span
                    className="w-3.5 h-3.5 rounded-full bg-green-500 shadow-sm"
                  ></span>Expenditure Categories
                </h2>

                <button
                  className="text-gray-500 hover:text-red-500 transition"
                  onClick={() => setShowCategoryList(false)}
                >
                  {FaTimes({ size: 18 })}
                </button>
              </div>

              <div className="p-4 max-h-[70vh] overflow-y-auto">
                <FullPageSpinner show={loading} />
                <Table
                  columns={categoryColumns}
                  data={categories.map((cat, index) => ({
                    ...cat,
                    createdAt: formatDate(cat.createdAt ?? ""),
                    serialNo: index + 1,
                  }))}
                  tableMenu={(row: ExpenditureCategory, closeMenu) => (
                    <TableMenu
                      row={row}
                      options={[
                        {
                          label: "Edit",
                          onClick: (ex: ExpenditureCategory) => {
                            openModal(
                              "Coming Soon 🚀",
                              "We’re still working on this feature. Stay tuned for the upcoming release.",
                              closeModal,
                              false,
                              "Close"
                            )
                          },
                        },
                        {
                          label: "Delete",
                          onClick: (ex: ExpenditureCategory) => {
                            deleteExpenditure(ex.id, "category");
                          },
                          color: "text-red-500",
                        },

                      ].filter(Boolean) as MenuOption[]}
                      closeMenu={closeMenu}
                    />
                  )} rowsPerPage={10}


                />
              </div>
            </div>
          </div>)}

      </div>
    </div>
  );
}
